import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { AlertCircle, Quote } from 'lucide-react';

const Prices = () => {
  const priceCategories = [
    {
      title: "Lille hund (1-11 kg / max 40 cm)",
      services: [
        {
          name: "Maskineklip & bad",
          price: "1.100,-",
          description: "Et udførligt maskinklip.\n\nEfterfølgende bad, tørring og styling. Inklusiv negleklip & øre-plukning.\n\nBehandlingen tager ca. én time.\n\nHvis der er ønske om ekstra længde (fx. på hovede, fane, eller hale), skal dette være redt ud inden ankomst. Hvis meget filtret pels, ekstra gebyr 150 kr."
        },
        {
          name: "Maskineklip minus bad",
          price: "850,-",
          description: "Et udførligt maskinklip.\n\nInklusiv negleklip & øre-plukning.\n\nBehandlingen tager ca. 30 min.\n\nHvis der er ønske om ekstra længde (fx. på hovede, fane, eller hale), skal dette være redt ud inden ankomst.\n\nOBS! Hvis længde på krop ønskes bevares, skal dette være udredt - Meget filret pels, ekstra gebyr på 150 kr."
        },
        {
          name: "Håndklip & bad",
          price: "1.500,-",
          description: "Et udførligt håndklip - til dem der ønsker længde og special-udtryk.\n\nEfterfølgende bad, tørring og styling. Inklusiv negleklip & øre-plukning.\n\nBehandlingen tager ca. 75 min.\n\nOBS! Hunden skal være udredt med kam inden ankomst.\n\nDet er ikke muligt at håndklippe, hvis der konstateres filt i hundens pels."
        },
        {
          name: "Studsning",
          price: "850,-",
          description: "Håndklip af ansigt, ben, poter samt hale på nyligt maskine/hånd-klippet hund (max 7 uger siden sidste besøg).\n\nStudsningen tager ca. 3o min.\n\nHunden skal være badet hjemmefra senest dagen før og hunden SKAL være redt igennem. Meget filtret pels, ekstra gebyr 150 kr."
        }
      ]
    },
    {
      title: "Mellem hund (11-19 kg / max 70 cm)",
      services: [
        {
          name: "Maskineklip & bad",
          price: "1.300,-",
          description: "Et udførligt maskinklip.\n\nEfterfølgende bad, tørring og styling. Inklusiv negleklip & øre-plukning.\n\nBehandlingen tager ca. én time.\n\nHvis der er ønske om ekstra længde (fx. på hovede, fane, eller hale), skal dette være redt ud inden ankomst. Meget filret pels, ekstra gebyr 150 kr."
        },
        {
          name: "Maskineklip minus bad",
          price: "1100,-",
          description: "Et udførligt maskinklip.\n\nInklusiv negleklip & øre-plukning.\n\nBehandlingen tager ca. 45 min.\n\nHvis der er ønske om ekstra længde (fx. på hovede, fane, eller hale), skal dette være redt ud inden ankomst.\n\nOBS! Hvis længde på krop ønskes bevares, skal dette være udredt - Meget filtret pels, ekstra gebyr på 150 kr."
        },
        {
          name: "Studsning",
          price: "1.100,-",
          description: "Håndklip af ansigt, ben, poter samt hale på nyligt maskineklippet hund (max 8 uger siden sidste besøg).\n\nStudsningen tager ca. 45 min.\n\nHunden skal være badet hjemmefra senest dagen før og hunden SKAL være redt igennem."
        }
      ]
    },
    {
      title: "Andet",
      services: [
        {
          name: "Negleklip",
          price: "250,-",
          description: "Din hund får klippet dens negle.\n\nNegleklip tager ca. 5 min og du behøver ikke bestille tid, bare giv mig et ring, så finder vi ud af hvornår det kan passe ind."
        }
      ]
    }
  ];

  return (
    <div className="bg-white">
      <Helmet>
        <title>Priser — Toftes Hundesalon</title>
        <meta name="description" content="Se vores priser på hundeklipning og pleje. Vi tilbyder gennemsigtige priser for både små og mellemstore hunde." />
        <meta property="og:title" content="Priser — Toftes Hundesalon" />
      </Helmet>

      {/* Header */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-6">Oversigt</h2>
          <h1 className="!text-[2rem] md:text-6xl font-serif italic text-stone-900 mb-10 leading-tight">Prisliste</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto italic">
            Her finder du en oversigt over vores behandlinger. Vi lægger vægt på kvalitet og din hunds tryghed.
          </p>
        </div>
      </section>

      {/* Price List */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-24">
            {priceCategories.map((category, idx) => (
              <div key={idx}>
                <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-12 border-b border-stone-100 pb-4">
                  {category.title}
                </h2>
                <div className="grid grid-cols-1 gap-12">
                  {category.services.map((service, sIdx) => (
                    <div key={sIdx} className="flex flex-col md:flex-row justify-between items-start gap-8 group">
                      <div className="max-w-2xl">
                        <h3 className="!text-[1.6rem] md:text-5xl font-serif italic text-stone-900 mb-4 group-hover:text-stone-700 transition-colors">
                          {service.name}
                        </h3>
                        <p className="text-gray-600 leading-relaxed italic whitespace-pre-line">{service.description}</p>
                      </div>
                      <div className="text-4xl font-serif italic text-stone-800 pt-2">
                        {service.price}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Important Info */}
          <div className="mt-24 bg-stone-100 text-stone-900 p-4 md:p-16 relative overflow-hidden border border-stone-200">
            <div className="absolute top-0 right-0 w-1/4 h-full bg-stone-200/50 -skew-x-12 translate-x-1/2" />
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-12">
              <div className="bg-stone-200 p-6">
                <AlertCircle className="w-10 h-10 text-stone-600" />
              </div>
              <div>
                <h3 className="text-xs uppercase tracking-widest font-bold mb-4 text-stone-600">Vigtig information</h3>
                <p className="text-2xl font-serif italic leading-relaxed text-stone-900">
                  Hvis jeg konstaterer hunden har lopper, opkræves der gebyr på 350,- kr.
                </p>
              </div>
              <div className="md:ml-auto">
                <a 
                  href="https://tofteshundesalon.planway.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-stone-800 text-white px-12 py-5 rounded-none text-xs uppercase tracking-widest font-bold hover:bg-stone-900 transition-all whitespace-nowrap"
                >
                  Book tid
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Image & Quote */}
      <section className="py-32 px-4 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col-reverse md:flex-row items-center gap-16 md:gap-24">
            <div className="flex-1 text-left">
              <Quote className="w-12 h-12 mb-10 text-stone-200 opacity-50" />
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif italic text-stone-900 leading-tight">
                “En hund er det eneste væsen på jorden, som elsker dig mere, end den elsker sig selv”
              </h2>
            </div>
            <div className="flex-1 w-full">
              <img 
                src="https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/dfe0c9b4-7254-4288-8b11-b586ee4b7b4f.jpg" 
                alt="Glad hund" 
                className="w-full rounded-none shadow-2xl transition-all duration-1000"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Prices;
